import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type EditorModalTab = 'tutorials' | string;

interface EditorModalContextValue {
  isOpen: boolean;
  initialTab: string;
  initialTutorialId: string | number | null;
  openEditorModal: (tab?: string, tutorialId?: string | number | null) => void;
  closeEditorModal: () => void;
}

const EditorModalContext = createContext<EditorModalContextValue | null>(null);

interface EditorModalProviderProps {
  children: React.ReactNode;
}

export function EditorModalProvider({ children }: EditorModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('tutorials');
  const [initialTutorialId, setInitialTutorialId] = useState<string | number | null>(null);
  const previousBodyOverflowRef = useRef<string | null>(null);
  const previousScrollYRef = useRef<number | null>(null);
  const previousBodyPositionRef = useRef<string | null>(null);
  const previousBodyTopRef = useRef<string | null>(null);

  const openEditorModal = useCallback((tab = 'tutorials', tutorialId: string | number | null = null) => {
    setInitialTab(tab);
    setInitialTutorialId(tutorialId);
    setIsOpen(true);

    if (previousBodyOverflowRef.current === null) {
      previousBodyOverflowRef.current = document.body.style.overflow ?? '';
      previousScrollYRef.current = window.scrollY;
      previousBodyPositionRef.current = document.body.style.position ?? '';
      previousBodyTopRef.current = document.body.style.top ?? '';

      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }
  }, []);

  const closeEditorModal = useCallback(() => {
    setIsOpen(false);

    if (previousBodyOverflowRef.current !== null) {
      document.body.style.overflow = previousBodyOverflowRef.current;
      document.body.style.position = previousBodyPositionRef.current;
      document.body.style.top = previousBodyTopRef.current;
      document.body.style.width = '';

      if (previousScrollYRef.current !== null) {
        window.scrollTo(0, previousScrollYRef.current);
      }

      previousBodyOverflowRef.current = null;
      previousScrollYRef.current = null;
      previousBodyPositionRef.current = null;
      previousBodyTopRef.current = null;
    }
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      initialTab,
      initialTutorialId,
      openEditorModal,
      closeEditorModal,
    }),
    [isOpen, initialTab, initialTutorialId, openEditorModal, closeEditorModal]
  );

  return <EditorModalContext.Provider value={value}>{children}</EditorModalContext.Provider>;
}

export function useEditorModal(): EditorModalContextValue {
  const context = useContext(EditorModalContext);
  if (!context) {
    throw new Error('useEditorModal deve ser usado dentro de um EditorModalProvider');
  }
  return context;
}
